/**
 * Login Component
 *
 * Handles user authentication with email and password using Supabase Auth
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Alert,
  Paper,
  CircularProgress,
  Divider
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState(false);

  const { login, loginWithGoogle, user, error: authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if there's a message from another page (e.g., after registration)
  const message = location.state?.message;

  // Determine redirect path after login
  const [redirectPath, setRedirectPath] = useState('');

  useEffect(() => {
    if (location.state?.returnTo) {
      setRedirectPath(location.state.returnTo);
    } else if (location.pathname === '/login' && location.state?.from === '/checkout') {
      setRedirectPath('/checkout');
    } else if (document.referrer && document.referrer.includes('/checkout')) {
      setRedirectPath('/checkout');
    } else {
      setRedirectPath('/account');
    }
  }, [location]);

  // Redirect once user is authenticated
  useEffect(() => {
    if (user) {
      setSuccess(true);
      const timer = setTimeout(() => {
        navigate(redirectPath || '/account');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [user, navigate, redirectPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    try {
      setLocalError('');
      setSuccess(false);
      setLoading(true);
      await login(email, password);
      setSuccess(true);
    } catch (err) {
      setSuccess(false);
      const msg = err.message || '';
      if (msg.includes('Invalid login credentials')) {
        setLocalError('Incorrect email or password. Please try again.');
      } else if (msg.includes('Email not confirmed')) {
        setLocalError('Your account has not been confirmed. Please check your email for a confirmation link.');
      } else {
        setLocalError(`Login failed: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const displayError = localError || authError;

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ color: '#8B4513' }}>
          Login
        </Typography>

        {message && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {message}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Login successful! Redirecting you to your account...
          </Alert>
        )}

        {displayError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {displayError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {/* Google sign-in above email/password form */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={loginWithGoogle}
            sx={{
              mb: 2,
              color: '#8B4513',
              borderColor: '#8B4513',
              '&:hover': { borderColor: '#6d3610', backgroundColor: 'rgba(139,69,19,0.04)' }
            }}
          >
            Continue with Google
          </Button>

          <Divider sx={{ mb: 2 }}>or</Divider>

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              bgcolor: '#8B4513',
              '&:hover': { bgcolor: '#6d3610' }
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
          </Button>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Link to="/register" style={{ textDecoration: 'none', color: '#8B4513' }}>
              <Typography variant="body2">
                Don't have an account? Register
              </Typography>
            </Link>

            <Link to="/forgot-password" style={{ textDecoration: 'none', color: '#8B4513' }}>
              <Typography variant="body2">
                Forgot password?
              </Typography>
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
